import {Injectable} from '@angular/core';
import {BehaviorSubject, from, map, Observable, of, throwError} from "rxjs";

import {Anagrafica, GetUserProfileDTO, LoginResponse} from "../../api-clients/generated/services";
import {JsBridgeService} from "./js-bridge.service";
import {AuthMode} from "../shared/enums/auth-mode.enum";


@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  checkLoggedUser$ = new BehaviorSubject(!!this.getAccessToken());
  checkUpdateProfile$ = new BehaviorSubject(!!this.getProfile());
  currentToken: any | null = null;

  profile: GetUserProfileDTO | undefined;
  anagrafiche: Anagrafica[] | undefined;
  refreshToken: string | null | undefined;
  accessToken: string | undefined | null;
  spidAccessToken: string | null = null;
  authMode: AuthMode | null = null;
  isMobilePlatform = false;
  deviceId: string | null = null;
  constructor(private jsBridgeService: JsBridgeService) {
    this.restoreFromSessionStorage();
  }

  private restoreFromSessionStorage() {
    const at = sessionStorage.getItem(StorageProperties.ACCESS_TOKEN);
    const rt = sessionStorage.getItem(StorageProperties.REFRESH_TOKEN);
    const spidAt = sessionStorage.getItem(StorageProperties.SPID_ACCESS_TOKEN);
    const am = sessionStorage.getItem(StorageProperties.AUTH_MODE);

    if (at) {
      this.accessToken = at;
      this.checkLoggedUser$.next(true);
    }
    if (rt) {
      this.refreshToken = rt;
    }
    if (spidAt) {
      this.spidAccessToken = spidAt;
    }
    if (am) {
      this.authMode = am as AuthMode;
    }
  }
  setDeviceId(id: string){
    this.deviceId = id;
  }
  //// ------ GET

  getAccessToken(): string | undefined | null {
    return this.accessToken;
  }

  getRefreshToken(): string | undefined | null {
    return this.refreshToken
  }

  getProfile(): GetUserProfileDTO | undefined {
    return this.profile;
  }

  getAnagrafiche(): Anagrafica[] | undefined {
    return this.anagrafiche;
  }

  getPlatform() {
    return localStorage.getItem(StorageProperties.PLATFORM);
  }

  get isLogged() {
    return !!this.getAccessToken()
  }

  //----MARK: GENERIC ----//

  // generic get
  async checkPreferenceToken(key: StorageProperties) {
    return localStorage.getItem(key);
  }

  // generic set
  async setPreferenceToken(key: StorageProperties, value: string) {
    localStorage.setItem(key, value);
  }

  // generic clean/remove
  async removePreferenceToken(key: StorageProperties) {
    localStorage.removeItem(key);
  }

  //----ENDMARK: GENERIC ----//

  //----MARK: FUNZIONI DI SET ----//

  setPreferenceProfile$(user: GetUserProfileDTO): Observable<GetUserProfileDTO> {
    if (!!user) {
      this.profile = user;
      this.checkUpdateProfile$.next(!!user);
      return of(user);
    } else {
      return throwError(() => new Error)
    }
  }

  setAnagrafiche$(anagrafiche: Anagrafica[]): Observable<Anagrafica[]> {
    this.anagrafiche = anagrafiche;
    return of(anagrafiche);
  }

  setAccessToken$(value: string) {
    this.accessToken = value;
    sessionStorage.setItem(StorageProperties.ACCESS_TOKEN, value);
    this.checkLoggedUser$.next(!!value);
    return of(value);
  }

  setRefreshToken$(value: string) {
    this.refreshToken = value;
    sessionStorage.setItem(StorageProperties.REFRESH_TOKEN, value);
    return of(value);
  }

  setExpires$(value: number) {
    return of(value);
  }

  setAuth$(auth: LoginResponse) {
    return this.setToken$(auth.access_token!, auth.refresh_token!, auth.expires_in!);
  }

  setToken$(access_token: string, refresh_token: string, expiration: number) {
    this.accessToken = access_token;
    this.refreshToken = refresh_token;
    this.checkLoggedUser$.next(!!access_token);
    return of([access_token, refresh_token, expiration]);
  }

  setSlugComune$(slug: string): Observable<void> {
    return from(this.setPreferenceToken(StorageProperties.SLUG_COMUNE_PRATICHE, slug));
  }

  getSlugComune$(): Observable<string | null> {
    return from(this.checkPreferenceToken(StorageProperties.SLUG_COMUNE_PRATICHE));
  }

  removeSlugComune$(): Observable<void> {
    return from(this.removePreferenceToken(StorageProperties.SLUG_COMUNE_PRATICHE));
  }

  //----MARK: SPID/PASSKEY ----//

  setAuthMode$(mode: AuthMode): Observable<void> {
    this.authMode = mode;
    sessionStorage.setItem(StorageProperties.AUTH_MODE, mode);
    return of(undefined);
  }

  getAuthMode(): AuthMode | null {
    return this.authMode;
  }

  setSpidAuth$(accessToken: string, refreshToken: string, expiresIn: number): Observable<any> {
    this.spidAccessToken = accessToken;
    this.refreshToken = refreshToken;
    sessionStorage.setItem(StorageProperties.SPID_ACCESS_TOKEN, accessToken);
    sessionStorage.setItem(StorageProperties.REFRESH_TOKEN, refreshToken);
    return of({accessToken, refreshToken, expiresIn});
  }

  getSpidAccessToken(): string | null {
    return this.spidAccessToken;
  }

  setPasskeyHint$(hint: {userId: string; displayName: string}): Observable<void> {
    return from(this.setPreferenceToken(StorageProperties.PASSKEY_HINT, JSON.stringify(hint)));
  }

  getPasskeyHint$(): Observable<{userId: string; displayName: string} | null> {
    return from(this.checkPreferenceToken(StorageProperties.PASSKEY_HINT)).pipe(
      map((value) => {
        if (!value) return null;
        try {
          return JSON.parse(value);
        } catch {
          return null;
        }
      })
    );
  }

  //----ENDMARK: SPID/PASSKEY ----//

  //----ENDMARK: FUNZIONI DI SET ----//
  getUserId(): string | null {
    return this.profile?.id ?? null;
  }
  //----MARK: FUNZIONI DI CLEAN ----//
  cleanPreferenceToken$() {
    const accessToken = this.getAccessToken();
    const userId = this.getUserId();

    if (accessToken && userId) {
      this.jsBridgeService.logout({ accessToken, userId });
    }

    this.profile = undefined;
    this.anagrafiche = undefined;
    this.accessToken = undefined;
    this.refreshToken = undefined;
    this.spidAccessToken = null;
    this.authMode = null;
    this.checkLoggedUser$.next(false);
    this.checkUpdateProfile$.next(false);

    sessionStorage.removeItem(StorageProperties.ACCESS_TOKEN);
    sessionStorage.removeItem(StorageProperties.REFRESH_TOKEN);
    sessionStorage.removeItem(StorageProperties.SPID_ACCESS_TOKEN);
    sessionStorage.removeItem(StorageProperties.AUTH_MODE);

    return of(true);
  }

  //----ENDMARK: FUNZIONI DI CLEAN ----//
}

export enum StorageProperties {
  TOKEN_RESPONSE = 'TOKEN_RESPONSE',
  ACCESS_TOKEN = 'access_token',
  REFRESH_TOKEN = 'refresh_token',
  EXPIRATION = 'expiration',
  PROFILE = 'user',
  PLATFORM = 'platform',
  SLUG_COMUNE_PRATICHE = 'slug_comune_pratiche',
  AUTH_MODE = 'auth_mode',
  SPID_ACCESS_TOKEN = 'spid_access_token',
  SPID_REFRESH_TOKEN = 'spid_refresh_token',
  SPID_EXPIRATION = 'spid_expiration',
  PASSKEY_HINT = 'passkey_hint',
  TEXT_SIZE = 'text_size',
}

export enum UserRolesEnum {
  ADMIN = 'admin',
}

export interface TokenI {
  realm_access: RealmAccessI;
}

export interface RealmAccessI {
  roles: string[];
}

